import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors } from '@angular/common/http';
import { DEFAULT_CURRENCY_CODE, ErrorHandler, importProvidersFrom } from '@angular/core';

import { AppComponent } from './app/app';
import { routes } from './app/app.routes';

// MSAL imports
import {
  MsalModule,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalInterceptorConfiguration,
} from '@azure/msal-angular';
import {
  IPublicClientApplication,
  PublicClientApplication,
  BrowserCacheLocation,
  InteractionType,
  LogLevel
} from '@azure/msal-browser';

import { environment } from './environments/environment';
import { b2cPolicies } from './app/b2c-config';
import { msalInterceptorFn } from './app/app-interceptors';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { loadingInterceptorFn } from './app/core/loader/loading.interceptor';
import { CurrencyMaskConfig, CurrencyMaskModule, CURRENCY_MASK_CONFIG } from 'ng2-currency-mask';
import { registerLocaleData } from '@angular/common';
import localePt from '@angular/common/locales/pt';
import { LOCALE_ID } from '@angular/core'; // Importe o LOCALE_ID
import { GlobalErrorHandlerService } from './app/core/interceptors/global-error-handle.service';
import { getSchoolFromUrl } from './app/utils/utils';

const isIE = window.navigator.userAgent.indexOf("MSIE ") > -1 || window.navigator.userAgent.indexOf("Trident/") > -1;

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap: new Map<string, Array<string>>([
      [environment.apiPortalPais, environment.scopes] // ← isso define os scopes
    ])
  };
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.azureAdClientId,
      authority: b2cPolicies.authorities.signUpSignIn.authority,
      redirectUri: (window.location.protocol === 'https:' ? environment.loginUrl : 'http://localhost:4200/'),
      postLogoutRedirectUri: (window.location.protocol === 'https:' ? environment.loginUrl : 'http://localhost:4200/')
       + `${(sessionStorage.getItem('school') ?? getSchoolFromUrl())}`, // ← isso define o redirecionamento após logout
      knownAuthorities: [b2cPolicies.authorityDomain],
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE,
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Error,
        piiLoggingEnabled: false
      }
    }
  });
}

export function MSALGuardConfigFactory() {
  return {
    interactionType: InteractionType.Redirect as any,
    authRequest: {
      scopes: environment.scopes
    },
    loginFailedRoute: '/login-failed'
  };
}

// Registre os dados de localização para o português do Brasil
registerLocaleData(localePt);

export const CustomCurrencyMaskConfig: CurrencyMaskConfig = {
    align: "right",
    allowNegative: true,
    decimal: ",",
    precision: 2,
    prefix: "R$ ",
    suffix: "",
    thousands: "."
};


const msalInstance = MSALInstanceFactory();
const msalGuardConfig = MSALGuardConfigFactory();
const msalInterceptorConfig = MSALInterceptorConfigFactory();

msalInstance.initialize()
  .then(() => {
    return bootstrapApplication(AppComponent, {
      providers: [
        provideRouter(routes),
        provideAnimations(),
        provideHttpClient(
          withInterceptors([msalInterceptorFn, loadingInterceptorFn])
        ),
        importProvidersFrom(
          MsalModule.forRoot(
            msalInstance,
            msalGuardConfig,
            msalInterceptorConfig
          )
        ),
        // Define o formato de números e datas para pt-BR
        {
          provide: LOCALE_ID,
          useValue: 'pt-BR'
        },
        // Define a MOEDA PADRÃO para Real Brasileiro
        {
          provide: DEFAULT_CURRENCY_CODE,
          useValue: 'BRL' // <--- ADICIONE ESTA LINHA
        },
        // Define o manipulador de erros global
        {
          provide: ErrorHandler,
          useClass: GlobalErrorHandlerService
        },
        {
          provide: CURRENCY_MASK_CONFIG,
          useValue: CustomCurrencyMaskConfig
        },
        {
          provide: MSAL_INSTANCE,
          useValue: msalInstance
        },
        {
          provide: MSAL_GUARD_CONFIG,
          useValue: msalGuardConfig
        },
        {
          provide: MSAL_INTERCEPTOR_CONFIG,
          useValue: msalInterceptorConfig
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService
      ],
    });
  })
  .catch((error) => {
    console.error('❌ Erro ao inicializar o MSAL:', error);
  });


